/**
 * JAI Brand System
 * Complete design system: logo, colors, typography, animations
 */

export const JAI_COLORS = {
  navy: '#0A1F44',
  navyDeep: '#04122E',
  blue: '#1E5BD6',
  blueBright: '#3A7CFF',
  paper: '#F6F8FB',
  ink: '#0A1F44',
  mute: 'rgba(10,31,68,0.55)',
  hair: 'rgba(10,31,68,0.12)',
  white: '#FFFFFF',
  success: '#1A8754',
  error: '#D63A3A',
};

export const JAI_FONTS = {
  tight: "'Inter Tight', Inter, system-ui, sans-serif",
  mono: "ui-monospace, 'SF Mono', Menlo, monospace",
};

// Italic J glyph path (120x120 viewBox)
const J_PATH = 'M 55 38 L 65 38 L 65 72 C 65 80 60 84 50 84 C 40 84 35 79 35 70 L 44 70 C 44 75 47 76 50 76 C 54 76 55 73 55 70 Z';

// ============================================================
// STATIC MARK (Lean Dial)
// ============================================================
export function JaiMark({ size = 120, bg = JAI_COLORS.navy, accent = JAI_COLORS.blue, fg = 'white', mono = false, className = '' }) {
  const r = 52;
  const arcAngle = 108;
  const rad = (arcAngle * Math.PI) / 180;
  const endX = 60 + r * Math.sin(rad);
  const endY = 60 - r * Math.cos(rad);
  const largeArc = arcAngle > 180 ? 1 : 0;

  const fillBg = mono ? JAI_COLORS.navy : bg;
  const fillFg = mono ? 'white' : fg;
  const fillAccent = mono ? 'white' : accent;
  const bezelStroke = fillFg === 'white' ? 'rgba(255,255,255,0.08)' : 'rgba(10,31,68,0.08)';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      fill="none"
      aria-label="JAI"
      className={className}
    >
      <circle cx="60" cy="60" r="54" fill={fillBg} />
      <circle cx="60" cy="60" r="48" fill="none" stroke={bezelStroke} strokeWidth="0.75" />
      <circle cx="60" cy="60" r={r} fill="none" stroke={fillAccent} strokeWidth="4" opacity="0.14" />
      <path
        d={`M 60 ${60 - r} A ${r} ${r} 0 ${largeArc} 1 ${endX} ${endY}`}
        fill="none"
        stroke={fillAccent}
        strokeWidth="4.5"
        strokeLinecap="round"
      />
      <circle cx={endX} cy={endY} r="2.6" fill={fillAccent} />
      <g transform="skewX(-8) translate(18 -1)">
        <path d={J_PATH} fill={fillFg} />
      </g>
    </svg>
  );
}

// ============================================================
// ANIMATED MARK (Launch/Loading)
// ============================================================
export function JaiMarkAnimated({ size = 120, bg = JAI_COLORS.navy, accent = JAI_COLORS.blueBright, fg = 'white' }) {
  const r = 52;
  const c = 2 * Math.PI * r;
  const arcLen = c * 0.78;
  const bezelStroke = 'rgba(255,255,255,0.08)';

  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none">
      <circle cx="60" cy="60" r="54" fill={bg} />
      <circle cx="60" cy="60" r="48" fill="none" stroke={bezelStroke} strokeWidth="0.75" />
      <circle cx="60" cy="60" r={r} fill="none" stroke={accent} strokeWidth="4" opacity="0.15" />
      <circle
        cx="60"
        cy="60"
        r={r}
        fill="none"
        stroke={accent}
        strokeWidth="4.5"
        strokeLinecap="round"
        strokeDasharray={`${arcLen} ${c}`}
        transform="rotate(-110 60 60)"
        style={{
          animation: 'jaiSweep 2.4s ease-in-out infinite',
        }}
      />
      <g transform="skewX(-8) translate(18 -1)">
        <path d={J_PATH} fill={fg} />
      </g>
      <style>{`
        @keyframes jaiSweep {
          0% { stroke-dasharray: 0 ${c}; }
          70% { stroke-dasharray: ${arcLen} ${c}; }
          100% { stroke-dasharray: ${arcLen} ${c}; }
        }
      `}</style>
    </svg>
  );
}

// ============================================================
// WORDMARK
// ============================================================
export function JaiWordmark({ size = 80, color = JAI_COLORS.navy, weight = 800 }) {
  return (
    <span
      style={{
        fontFamily: JAI_FONTS.tight,
        fontWeight: weight,
        fontSize: size,
        letterSpacing: '-0.04em',
        color,
        lineHeight: 0.9,
        display: 'inline-block',
      }}
    >
      JAI
    </span>
  );
}

// ============================================================
// LOCKUPS
// ============================================================
export function JaiLockupHorizontal({ markSize = 80, wordmarkSize = 72, bg = 'transparent', gap = 16, flexDirection = 'row' }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection,
        alignItems: 'center',
        gap,
        background: bg,
      }}
    >
      <JaiMark size={markSize} />
      <JaiWordmark size={wordmarkSize} />
    </div>
  );
}

export function JaiLockupReversed({ markSize = 80, wordmarkSize = 72 }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
      }}
    >
      <JaiMark size={markSize} bg="white" fg={JAI_COLORS.navy} accent={JAI_COLORS.blueBright} />
      <JaiWordmark size={wordmarkSize} color="white" />
    </div>
  );
}

// ============================================================
// SPLASH SCREENS
// ============================================================
export function JaiLoadingScreen({ text = 'Loading your leagues...' }) {
  return (
    <div
      style={{
        height: 'calc(100vh / var(--jai-zoom))',
        background: JAI_COLORS.navyDeep,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 18,
      }}
    >
      <JaiMarkAnimated size={130} />
      <div
        style={{
          fontFamily: JAI_FONTS.mono,
          fontSize: 11,
          letterSpacing: '0.32em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.45)',
        }}
      >
        {text}
      </div>
    </div>
  );
}

export function JaiHeroSection() {
  return (
    <div
      style={{
        height: 420,
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${JAI_COLORS.navy} 0%, ${JAI_COLORS.navyDeep} 100%)`,
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(circle at 80% 30%, rgba(58,124,255,0.22), transparent 55%)`,
        }}
      />
      <div style={{ position: 'absolute', right: 48, top: '50%', transform: 'translateY(-50%)', opacity: 0.95 }}>
        <JaiMark size={300} accent={JAI_COLORS.blueBright} />
      </div>
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          padding: '56px 56px',
          maxWidth: 620,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
          <JaiMark size={28} accent={JAI_COLORS.blueBright} />
          <JaiWordmark size={20} color="white" />
        </div>
        <h1
          style={{
            fontSize: 44,
            fontWeight: 700,
            letterSpacing: '-0.03em',
            color: 'white',
            margin: 0,
            lineHeight: 1.05,
            fontFamily: JAI_FONTS.tight,
          }}
        >
          Fantasy advice that actually <span style={{ color: JAI_COLORS.blueBright }}>knows your league.</span>
        </h1>
        <p
          style={{
            fontSize: 14,
            color: 'rgba(255,255,255,0.7)',
            lineHeight: 1.55,
            marginTop: 18,
            maxWidth: 440,
            fontFamily: JAI_FONTS.tight,
          }}
        >
          AI-powered draft rankings tailored to your roster, scoring, and pick. Across every sport.
        </p>
        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button
            style={{
              background: 'white',
              color: JAI_COLORS.navy,
              border: 'none',
              padding: '11px 20px',
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 600,
              fontFamily: JAI_FONTS.tight,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => (e.target.opacity = '0.9')}
          >
            Get early access →
          </button>
          <button
            style={{
              background: 'transparent',
              color: 'white',
              border: `1px solid rgba(255,255,255,0.25)`,
              padding: '11px 20px',
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 600,
              fontFamily: JAI_FONTS.tight,
              cursor: 'pointer',
            }}
          >
            How it works
          </button>
        </div>
      </div>
    </div>
  );
}

export function JaiMobileSplash() {
  return (
    <div
      style={{
        height: 540,
        background: `linear-gradient(180deg, ${JAI_COLORS.navy} 0%, ${JAI_COLORS.navyDeep} 100%)`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      <div style={{ position: 'absolute', top: 18, display: 'flex', justifyContent: 'center', left: 0, right: 0 }}>
        <div style={{ width: 80, height: 22, borderRadius: 12, background: 'rgba(0,0,0,0.4)' }} />
      </div>
      <JaiMarkAnimated size={104} />
      <div style={{ marginTop: 28 }}>
        <JaiWordmark size={56} color="white" />
      </div>
      <div
        style={{
          marginTop: 10,
          fontSize: 12,
          fontWeight: 500,
          letterSpacing: '0.32em',
          color: 'rgba(255,255,255,0.55)',
          textTransform: 'uppercase',
          fontFamily: JAI_FONTS.mono,
        }}
      >
        Smarter Fantasy Picks
      </div>
      <div style={{ position: 'absolute', bottom: 32, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: 120, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.25)' }} />
      </div>
    </div>
  );
}

// ============================================================
// BRAND SHEET
// ============================================================
export function JaiBrandSheet() {
  return (
    <div
      style={{
        borderBottom: `1px solid ${JAI_COLORS.hair}`,
        padding: '56px 64px',
        background: JAI_COLORS.paper,
        fontFamily: JAI_FONTS.tight,
      }}
    >
      <div style={{ marginBottom: 36, maxWidth: 800 }}>
        <div
          style={{
            fontFamily: JAI_FONTS.mono,
            fontSize: 11,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: JAI_COLORS.mute,
            marginBottom: 10,
          }}
        >
          JAI BRAND SYSTEM
        </div>
        <h2
          style={{
            fontSize: 36,
            fontWeight: 700,
            letterSpacing: '-0.025em',
            color: JAI_COLORS.ink,
            margin: 0,
            lineHeight: 1.05,
          }}
        >
          Design System & Guidelines
        </h2>
      </div>

      <div style={{ marginBottom: 40 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: JAI_COLORS.navy, marginBottom: 16 }}>Color Palette</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14 }}>
          <ColorSwatch name="Navy" hex={JAI_COLORS.navy} note="Primary" />
          <ColorSwatch name="Blue" hex={JAI_COLORS.blue} note="Accent" />
          <ColorSwatch name="Blue Bright" hex={JAI_COLORS.blueBright} note="On-dark" />
          <ColorSwatch name="Paper" hex={JAI_COLORS.paper} fg={JAI_COLORS.navy} note="Surface" />
          <ColorSwatch name="White" hex={JAI_COLORS.white} fg={JAI_COLORS.navy} note="Reversal" />
        </div>
      </div>

      <div style={{ marginBottom: 40 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: JAI_COLORS.navy, marginBottom: 16 }}>Typography</div>
        <TypographyRow family={JAI_FONTS.tight} weight={800} label="Wordmark" sample="JAI" />
        <TypographyRow family={JAI_FONTS.tight} weight={700} label="Display" sample="Smarter picks." />
        <TypographyRow family={JAI_FONTS.tight} weight={500} label="Body" sample="Built for every league." />
        <TypographyRow family={JAI_FONTS.mono} weight={500} label="Mono" sample="WK 04 · MON 8:30PM" />
      </div>

      <div style={{ marginBottom: 40 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: JAI_COLORS.navy, marginBottom: 16 }}>Clearspace & Scale</div>
        <div
          style={{
            background: 'white',
            borderRadius: 16,
            border: `1px solid ${JAI_COLORS.hair}`,
            padding: 32,
            display: 'flex',
            alignItems: 'center',
            gap: 32,
            flexWrap: 'wrap',
          }}
        >
          {[16, 24, 32, 48, 64, 96].map((size) => (
            <div key={size} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <JaiMark size={size} />
              <span style={{ fontSize: 9, color: JAI_COLORS.mute, fontFamily: JAI_FONTS.mono }}>{size}px</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// UTILITIES
// ============================================================
function ColorSwatch({ name, hex, fg = 'white', note }) {
  return (
    <div style={{ flex: 1 }}>
      <div
        style={{
          height: 130,
          background: hex,
          borderRadius: 12,
          display: 'flex',
          alignItems: 'flex-end',
          padding: 14,
          color: fg,
          fontFamily: JAI_FONTS.mono,
          fontSize: 11,
          border: hex === '#FFFFFF' ? `1px solid ${JAI_COLORS.hair}` : 'none',
        }}
      >
        <div>
          <div style={{ fontWeight: 700, letterSpacing: '0.04em' }}>{name}</div>
          <div style={{ opacity: 0.7, marginTop: 2 }}>{hex}</div>
        </div>
      </div>
      {note && (
        <div style={{ fontSize: 11, color: JAI_COLORS.mute, marginTop: 8, lineHeight: 1.4, fontFamily: JAI_FONTS.mono }}>
          {note}
        </div>
      )}
    </div>
  );
}

function TypographyRow({ family, weight, label, sample }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 24,
        padding: '20px 0',
        borderBottom: `1px solid ${JAI_COLORS.hair}`,
      }}
    >
      <div style={{ width: 180, flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: JAI_COLORS.navy }}>{label}</div>
        <div style={{ fontSize: 10, color: JAI_COLORS.mute, fontFamily: JAI_FONTS.mono, marginTop: 4 }}>
          {weight}
        </div>
      </div>
      <div
        style={{
          fontFamily: family,
          fontWeight: weight,
          fontSize: 32,
          color: JAI_COLORS.navy,
          letterSpacing: '-0.02em',
          lineHeight: 1,
        }}
      >
        {sample}
      </div>
    </div>
  );
}
